import { useEffect } from 'react';
import { fontLinkId, googleFontHref, themeFontFamilies, type ThemeStylesDto } from '@xshop/shared';

// The fonts a theme names, loaded from Google Fonts as a Shopify theme's font library loads them: a store that
// chooses Oswald for headings and Rubik for text gets Oswald and Rubik, not the browser's fallback. The page
// carries the theme's own families already when the server wrote them into its head; any other gets a stylesheet of
// its own, asking for the weights a theme uses and falling back to the regular weight when the family has fewer.

// Lists the families a theme names that the page still has to load, each once (the shared rule the server's head uses).
export const fontsToLoad = (styles: ThemeStylesDto): string[] => themeFontFamilies(styles);

// Adds one family's stylesheet unless the page has it, retrying with the regular weight if Google refuses the weights.
function loadFamily(family: string): void {
  const id = fontLinkId(family);
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  // Read with CORS, like the server's link, so a picture of the page can embed the font.
  link.crossOrigin = 'anonymous';
  link.href = googleFontHref(family, true);
  link.onerror = () => { link.onerror = null; link.href = googleFontHref(family, false); };
  document.head.appendChild(link);
}

// Loads the fonts a theme's styles name, once each, whenever the styles change.
export function useThemeFonts(styles: ThemeStylesDto | undefined): void {
  const families = styles ? fontsToLoad(styles).join('|') : '';
  useEffect(() => {
    for (const family of families.split('|')) if (family) loadFamily(family);
  }, [families]);
}
